import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type GetSupportAccessResult } from '@whinerp/client';
import { Button, Panel, SectionLabel, Skeleton, ErrorState } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { formatDateTime } from '@whinerp/contracts/format';

/**
 * Who from support may come in, and who has (F-144).
 *
 * The customer's side of the console. Everything the vendor can do to this
 * workspace starts with a row written here, by somebody who works here - there
 * is no route anywhere that lets the console grant itself access.
 *
 * The four states are drawn as four sentences rather than a switch that is on
 * or off. "Never let in", "let in until Friday", "withdrawn on Tuesday" and
 * "lapsed last month" are four different things to know, and three of them
 * would read as "off".
 */
export function SupportAccessPanel(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [hours, setHours] = useState('8');

  const access = useQuery<GetSupportAccessResult, ApiError>({
    queryKey: ['support-access'],
    queryFn: () => api.getSupportAccess({}),
  });

  const refresh = (): Promise<unknown> =>
    queryClient.invalidateQueries({ queryKey: ['support-access'] });

  const letIn = useMutation<unknown, ApiError>({
    mutationFn: () => api.letSupportIn({ body: { hours: Number(hours) } }),
    onSuccess: () => refresh(),
  });

  const withdraw = useMutation<unknown, ApiError>({
    mutationFn: () => api.withdrawSupportAccess({}),
    onSuccess: () => refresh(),
  });

  if (access.isPending) return <Skeleton className="h-block-md w-full" />;
  /*
   * A failure is not a loading state.
   *
   * These returned a Skeleton, so a panel whose request failed showed a
   * shimmer for ever - a screen that never loads rather than one that says
   * what went wrong, and a `settled()` that never settles (D-365).
   */
  if (access.isError) {
    return (
      <ErrorState
        title={t('state.error')}
        body={access.error.message}
        onRetry={() => void access.refetch()}
      />
    );
  }

  const { state, visits } = access.data;
  const failure = letIn.error ?? withdraw.error;
  const when = (value: string): string => formatDateTime(value, format);

  return (
    <Panel data-region="support-access" id="support-access">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <SectionLabel>{t('support.title')}</SectionLabel>
          <p className="text-meta text-ink-meta">{t('support.body')}</p>
        </div>

        {failure ? (
          <p
            data-support-refusal
            className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text"
          >
            {failure.message}
          </p>
        ) : null}

        <p data-support-state={state.state} className="text-dense text-ink">
          {state.state === 'open'
            ? t('support.state.open', { values: { when: when(state.expiresAt) } })
            : state.state === 'withdrawn'
              ? t('support.state.withdrawn', { values: { when: when(state.withdrawnAt) } })
              : state.state === 'lapsed'
                ? t('support.state.lapsed', { values: { when: when(state.lapsedAt) } })
                : t('support.state.never')}
        </p>

        <div className="flex flex-wrap items-end gap-2">
          <label className="flex items-center gap-2 text-dense text-ink-600">
            {t('support.hours')}
            <input
              data-support-hours
              inputMode="numeric"
              className="w-col-count rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
              value={hours}
              onChange={(event) => setHours(event.target.value)}
            />
          </label>
          <Button
            intent="secondary"
            size="small"
            data-support-let-in
            onClick={() => letIn.mutate()}
          >
            {t('support.let')}
          </Button>
          {state.state === 'open' ? (
            <Button
              intent="ghost"
              size="small"
              data-support-withdraw
              onClick={() => withdraw.mutate()}
            >
              {t('support.withdraw')}
            </Button>
          ) : null}
        </div>

        <div className="flex flex-col gap-1">
          <p className="text-micro uppercase tracking-label text-ink-meta">{t('support.visits')}</p>
          {visits.length === 0 ? (
            <p data-support-visits-empty className="text-meta text-ink-meta">
              {t('support.visits.none')}
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {visits.map((visit) => (
                <li
                  key={visit.impersonationId}
                  data-support-visit={visit.impersonationId}
                  className="flex flex-col gap-0.5 border-t border-t-rule pt-1 first:border-t-0 first:pt-0"
                >
                  <span className="text-dense text-ink">
                    {visit.staff} · {visit.actingAs}
                  </span>
                  {/* The reason, in the customer's list rather than only in
                      the vendor's. A record of a visit that does not say why
                      is a record nobody can question. */}
                  <span className="text-meta text-ink-600">{visit.reason}</span>
                  <span className="text-meta text-ink-meta">
                    {when(visit.startedAt)}
                    {visit.endedAt === null ? ` · ${t('support.visit.ongoing')}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Panel>
  );
}
